import type {ReactElement} from 'react';
import {useState} from 'react';
import {toast} from 'react-toastify';

import ConfirmationModal from '@/components/Modals/ConfirmationModal';
import {kycService} from '@/services/kycService';
import {AfriexVerificationStatus} from '@/types';
import {getResponseMessage} from '@/utils/errorMessage';

import ComplianceChecklistModal from './RequestDocumentsModal';
import type {UserDetailsReturnProps} from './types';
import UpdateUserInfoModal from './UpdateUserInfoModal';
import UserDetails from './UserDetails';

type UserOverviewProps = Pick<
  UserDetailsReturnProps,
  | 'user'
  | 'isLoadingUserDetails'
  | 'showModal'
  | 'toggleModal'
  | 'showComplianceModal'
  | 'toggleComplianceModal'
  | 'fetchUserDetails'
>;
const UserOverview = ({
  isLoadingUserDetails,
  user,
  showModal,
  toggleModal,
  showComplianceModal,
  toggleComplianceModal,
  fetchUserDetails,
}: UserOverviewProps): ReactElement => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const isVerified = user?.kyc?.status === AfriexVerificationStatus.Success;
  const verifyStatus = isVerified
    ? AfriexVerificationStatus.Failed
    : AfriexVerificationStatus.Success;

  const verifyAction = async () => {
    setLoading(!loading);
    try {
      await kycService.verifyUser(user.id, verifyStatus);
      toast.success(
        `Successfully ${isVerified ? 'unverified' : 'verified'} user!`,
      );
      setShowConfirmModal(false);
    } catch (error) {
      toast.error(
        getResponseMessage(error) ||
          `Failed to ${isVerified ? 'unverify' : 'verify'} user!`,
      );
      console.error(error);
    } finally {
      setLoading(false);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await fetchUserDetails();
    }
  };

  return (
    <div className="px-3 pt-3 mt-3">
      <UserDetails
        user={user}
        isLoadingUserDetails={isLoadingUserDetails}
        toggleModal={toggleModal}
        setShowConfirmModal={setShowConfirmModal}
        toggleComplianceModal={toggleComplianceModal}
      />

      {/* modal for BackID */}
      {showModal && (
        <UpdateUserInfoModal
          isOpen={showModal}
          user={user}
          onClose={toggleModal}
        />
      )}
      {/* modal for Request Documents */}
      {showComplianceModal && (
        <ComplianceChecklistModal
          isOpen={showComplianceModal}
          onClose={toggleComplianceModal}
          user={user}
        />
      )}

      <ConfirmationModal
        isOpen={showConfirmModal}
        label={
          <>
            Are you sure you want to{' '}
            <span
              className={
                !isVerified ? 'text-successful-500' : 'text-failed-500'
              }
            >
              {isVerified ? 'unverify' : 'verify'}
            </span>
            <br />
            <span className="font-semibold">{user.name.fullName}</span>
          </>
        }
        cancelAction={() => setShowConfirmModal(false)}
        cancelActionLabel="No"
        confirmAction={verifyAction}
        confirmActionLabel="Yes"
        loadingLabel={isVerified ? 'Unverifying...' : 'Verifying...'}
        loading={loading}
        title={`${isVerified ? 'Unverify' : 'Verify'} ${user.name.fullName}`}
      />
    </div>
  );
};

export default UserOverview;
